// ─────────────────────────────────────────────────────────────────────────────
// GLB LOADER + RIGGING SYSTEM — Phase 5 & 6
// Loads .glb garments via Three.js GLTFLoader
// Maps MediaPipe 33 joints → Three.js skeleton bones
// Standard: https://github.com/KhronosGroup/glTF
// ─────────────────────────────────────────────────────────────────────────────
import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import type { NormalizedLandmark } from "./pose";
import { POSE_IDX } from "./pose";

// ── GLTF bone name conventions → MediaPipe landmark mapping ──────────────────
// Standard humanoid rig bone names (Mixamo, VRM, Blender Rigify conventions)
// Used for future multi-bone full-body rigs; core 4-bone rig handled in rigGarmentFromPose
export const BONE_TO_MEDIAPIPE: Record<string, number> = {
  // Spine
  "Hips":           23, // left hip midpoint proxy
  "Spine":          23,
  "Spine1":         23,
  "Spine2":         11, // shoulder midpoint proxy
  "Neck":           0,  // nose
  "Head":           0,

  // Arms L
  "LeftArm":        POSE_IDX.LEFT_SHOULDER,
  "LeftForeArm":    POSE_IDX.LEFT_ELBOW,
  "LeftHand":       POSE_IDX.LEFT_WRIST,
  "LeftShoulder":   POSE_IDX.LEFT_SHOULDER,

  // Arms R
  "RightArm":       POSE_IDX.RIGHT_SHOULDER,
  "RightForeArm":   POSE_IDX.RIGHT_ELBOW,
  "RightHand":      POSE_IDX.RIGHT_WRIST,
  "RightShoulder":  POSE_IDX.RIGHT_SHOULDER,

  // Legs L
  "LeftUpLeg":      POSE_IDX.LEFT_HIP,
  "LeftLeg":        POSE_IDX.LEFT_KNEE,
  "LeftFoot":       27, // left ankle

  // Legs R
  "RightUpLeg":     POSE_IDX.RIGHT_HIP,
  "RightLeg":       POSE_IDX.RIGHT_KNEE,
  "RightFoot":      28, // right ankle

  // Mixamo prefix variants
  "mixamorig:Hips":         23,
  "mixamorig:Spine":        23,
  "mixamorig:Spine1":       23,
  "mixamorig:Spine2":       11,
  "mixamorig:Neck":         0,
  "mixamorig:Head":         0,
  "mixamorig:LeftArm":      POSE_IDX.LEFT_SHOULDER,
  "mixamorig:LeftForeArm":  POSE_IDX.LEFT_ELBOW,
  "mixamorig:LeftHand":     POSE_IDX.LEFT_WRIST,
  "mixamorig:RightArm":     POSE_IDX.RIGHT_SHOULDER,
  "mixamorig:RightForeArm": POSE_IDX.RIGHT_ELBOW,
  "mixamorig:RightHand":    POSE_IDX.RIGHT_WRIST,
  "mixamorig:LeftUpLeg":    POSE_IDX.LEFT_HIP,
  "mixamorig:LeftLeg":      POSE_IDX.LEFT_KNEE,
  "mixamorig:RightUpLeg":   POSE_IDX.RIGHT_HIP,
  "mixamorig:RightLeg":     POSE_IDX.RIGHT_KNEE,
};

export interface LoadedGarment {
  scene: THREE.Group;
  skeleton: THREE.Skeleton | null;
  bones: Map<string, THREE.Bone>;
  /** Normalized garment center at rest pose */
  boundingBox: THREE.Box3;
  type: "top" | "bottom" | "shoe" | "accessory" | "unknown";
}

export interface GarmentRigUpdate {
  landmarks: NormalizedLandmark[];
  canvasW: number;
  canvasH: number;
  isMirrored: boolean;
}

// Singleton loader setup
let _loader: GLTFLoader | null = null;

function getLoader(): GLTFLoader {
  if (_loader) return _loader;
  _loader = new GLTFLoader();

  // DRACO compression support (common in glTF garment assets)
  const draco = new DRACOLoader();
  draco.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.7/");
  _loader.setDRACOLoader(draco);

  return _loader;
}

// ── Load a GLB garment ────────────────────────────────────────────────────────
export async function loadGLBGarment(url: string): Promise<LoadedGarment> {
  const loader = getLoader();

  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (gltf) => {
        const scene = gltf.scene;
        scene.traverse(obj => {
          if (obj instanceof THREE.Mesh) {
            obj.castShadow = false;
            obj.receiveShadow = false;
            // Enable double-sided rendering for thin cloth
            if (obj.material instanceof THREE.Material) {
              obj.material.side = THREE.DoubleSide;
            }
          }
        });

        // Extract skeleton
        let skeleton: THREE.Skeleton | null = null;
        const bones = new Map<string, THREE.Bone>();
        scene.traverse(obj => {
          if (obj instanceof THREE.SkinnedMesh && obj.skeleton) {
            skeleton = obj.skeleton;
            for (const bone of obj.skeleton.bones) {
              bones.set(bone.name, bone);
              // Also index without prefix
              const shortName = bone.name.replace("mixamorig:", "");
              bones.set(shortName, bone);
            }
          }
        });

        // Compute bounding box
        const box = new THREE.Box3().setFromObject(scene);

        // Infer garment type from URL/name
        const urlLower = url.toLowerCase();
        let type: LoadedGarment["type"] = "unknown";
        if (urlLower.includes("shirt") || urlLower.includes("jacket") ||
            urlLower.includes("top") || urlLower.includes("hoodie") ||
            urlLower.includes("coat") || urlLower.includes("vest")) {
          type = "top";
        } else if (urlLower.includes("pant") || urlLower.includes("jean") ||
                   urlLower.includes("skirt") || urlLower.includes("short")) {
          type = "bottom";
        } else if (urlLower.includes("shoe") || urlLower.includes("boot") ||
                   urlLower.includes("sneaker")) {
          type = "shoe";
        } else if (urlLower.includes("hat") || urlLower.includes("bag") ||
                   urlLower.includes("watch") || urlLower.includes("accessory")) {
          type = "accessory";
        }

        resolve({ scene, skeleton, bones, boundingBox: box, type });
      },
      undefined,
      reject
    );
  });
}

// ── Coordinate conversion: MediaPipe (0–1) → world space (-2 → 2) ────────────
// Spec: toWorld(x, y) = { x: (x-0.5)*4, y: -(y-0.5)*4 }
function toWorld(
  x: number,
  y: number,
  isMirrored = false
): { x: number; y: number } {
  const nx = isMirrored ? 1 - x : x;
  return {
    x: (nx - 0.5) * 4,
    y: -(y - 0.5) * 4,
  };
}

// ── Scale: shoulder-to-shoulder distance × 5 ──────────────────────────────────
// Spec: dist = sqrt(dx²+dy²); scale = dist * 5
function getBodyScale(
  ls: NormalizedLandmark,
  rs: NormalizedLandmark
): number {
  const dx = ls.x - rs.x;
  const dy = ls.y - rs.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  return dist * 5;
}

// ── Drive bones from MediaPipe landmarks ──────────────────────────────────────
export function rigGarmentFromPose(
  garment: LoadedGarment,
  update: GarmentRigUpdate
) {
  const { landmarks, isMirrored } = update;
  const { bones } = garment;

  if (!bones.size) return; // No skeleton — 2D shader mode handles it

  const ls = landmarks[POSE_IDX.LEFT_SHOULDER];
  const rs = landmarks[POSE_IDX.RIGHT_SHOULDER];
  const lh = landmarks[POSE_IDX.LEFT_HIP];
  const rh = landmarks[POSE_IDX.RIGHT_HIP];

  if (!ls || !rs) return;

  // ── 1. Scale garment to body (spec: dist * 5) ────────────────────────────
  const scale = getBodyScale(ls, rs);
  garment.scene.scale.setScalar(scale);

  // ── 2. Drive key bones with toWorld coordinate conversion ────────────────
  // Root: Hips → hip midpoint
  if (lh && rh) {
    const hipMidX = (lh.x + rh.x) / 2;
    const hipMidY = (lh.y + rh.y) / 2;
    const hipPos = toWorld(hipMidX, hipMidY, isMirrored);
    const hipBone = bones.get("Hips");
    if (hipBone) {
      hipBone.position.set(hipPos.x, hipPos.y, 0);
    }
  }

  // Spine → shoulder midpoint
  const spineMidX = (ls.x + rs.x) / 2;
  const spineMidY = (ls.y + rs.y) / 2;
  const spinePos = toWorld(spineMidX, spineMidY, isMirrored);
  const spineBone = bones.get("Spine") ?? bones.get("Spine1") ?? bones.get("Spine2");
  if (spineBone) {
    spineBone.position.set(spinePos.x, spinePos.y, 0);
  }

  // LeftShoulder
  const leftPos = toWorld(ls.x, ls.y, isMirrored);
  const leftBone = bones.get("LeftShoulder") ?? bones.get("LeftArm");
  if (leftBone && (ls.visibility ?? 1) > 0.3) {
    leftBone.position.set(leftPos.x, leftPos.y, 0);
  }

  // RightShoulder
  const rightPos = toWorld(rs.x, rs.y, isMirrored);
  const rightBone = bones.get("RightShoulder") ?? bones.get("RightArm");
  if (rightBone && (rs.visibility ?? 1) > 0.3) {
    rightBone.position.set(rightPos.x, rightPos.y, 0);
  }

  // ── 3. Arm rotations (upper arm → elbow direction) ───────────────────────
  for (const side of ["Left", "Right"] as const) {
    const shoulder = landmarks[side === "Left" ? POSE_IDX.LEFT_SHOULDER : POSE_IDX.RIGHT_SHOULDER];
    const elbow    = landmarks[side === "Left" ? POSE_IDX.LEFT_ELBOW    : POSE_IDX.RIGHT_ELBOW];
    const wrist    = landmarks[side === "Left" ? POSE_IDX.LEFT_WRIST    : POSE_IDX.RIGHT_WRIST];

    if (!shoulder || !elbow || !wrist) continue;

    const upperArmBone = bones.get(`${side}Arm`);
    const foreArmBone  = bones.get(`${side}ForeArm`);

    if (upperArmBone && (shoulder.visibility ?? 1) > 0.3 && (elbow.visibility ?? 1) > 0.3) {
      const dir = new THREE.Vector3(
        elbow.x - shoulder.x,
        -(elbow.y - shoulder.y),
        elbow.z - shoulder.z
      ).normalize();
      upperArmBone.quaternion.setFromUnitVectors(
        new THREE.Vector3(side === "Left" ? -1 : 1, 0, 0),
        dir
      );
    }

    if (foreArmBone && (elbow.visibility ?? 1) > 0.3 && (wrist.visibility ?? 1) > 0.3) {
      const dir = new THREE.Vector3(
        wrist.x - elbow.x,
        -(wrist.y - elbow.y),
        wrist.z - elbow.z
      ).normalize();
      foreArmBone.quaternion.setFromUnitVectors(
        new THREE.Vector3(side === "Left" ? -1 : 1, 0, 0),
        dir
      );
    }
  }
}

// ── Multi-layer garment manager ───────────────────────────────────────────────
// Handles shirt + jacket + accessories simultaneously
export class GarmentLayerManager {
  private layers = new Map<string, {
    garment: LoadedGarment;
    scene: THREE.Group;
    layer: number;
  }>();

  constructor(private threeScene: THREE.Scene) {}

  async add(id: string, url: string, layer = 0): Promise<LoadedGarment> {
    // Remove existing if re-adding
    this.remove(id);

    const garment = await loadGLBGarment(url);
    garment.scene.renderOrder = layer;
    this.threeScene.add(garment.scene);
    this.layers.set(id, { garment, scene: garment.scene, layer });
    return garment;
  }

  remove(id: string) {
    const entry = this.layers.get(id);
    if (entry) {
      this.threeScene.remove(entry.scene);
      this.layers.delete(id);
    }
  }

  update(landmarks: NormalizedLandmark[], canvasW: number, canvasH: number, isMirrored: boolean) {
    for (const { garment } of this.layers.values()) {
      rigGarmentFromPose(garment, { landmarks, canvasW, canvasH, isMirrored });
    }
  }

  clear() {
    for (const id of this.layers.keys()) this.remove(id);
  }

  get count() { return this.layers.size; }
}
